import {useIsAuthenticated, useMsal} from '@azure/msal-react'
import {useSignal} from '@preact/signals-react'
import {useSignalEffect, useSignals} from '@preact/signals-react/runtime'
import {Badge} from 'primereact/badge'
import {Button} from 'primereact/button'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Tag} from 'primereact/tag'
import React, {useCallback, useMemo, useRef} from 'react'
import ReactFlagsSelect from 'react-flags-select'
import {useTranslation} from 'react-i18next'
import {Link, useHistory, useLocation} from 'react-router-dom'
import {
    getCollapsedAppMenu,
    getMobileAppMenuActive,
    setCollapsedAppMenu,
    setMobileAppMenuActive,
} from './AppUtils'
import {ReactComponent as CalendarIcon} from './assets/icons/CalendarIcon.svg'
import {ReactComponent as FaHospitalAlt} from './assets/icons/FaHospitalAlt.svg'
import {ReactComponent as FaUserCircle} from './assets/icons/FaUserCircle.svg'
import DoctorIcon from './assets/icons/HCP.png'
import {ReactComponent as MdPlayLesson} from './assets/icons/MdPlayLesson.svg'
import PatientIcon from './assets/icons/Patient.png'
import {ReactComponent as RiLockPasswordFill} from './assets/icons/RiLockPasswordFill.svg'
import {ReactComponent as RiLogoutCircleRFill} from './assets/icons/RiLogoutCircleRFill.svg'
import SuperAdminIcon from './assets/icons/SuperAdmin.png'
import AdminIcon from './assets/icons/SuperUser.png'
import {ReactComponent as Logo} from './assets/icons/TaVIE-Logo.svg'
import {ReactComponent as VscReport} from './assets/icons/VscReport.svg'
import {revokeToken} from './components/authentication/AuthEndPoints'
import {useAuth} from './components/authentication/AuthProvider'
import {
    clearStorage,
    getRefreshToken,
    getSuperAdminRole,
    removeClientId,
} from './components/authentication/AuthUtils'
import {hospitalDisplaySettingType} from './components/hospital/HospitalConstant'
import {useNotificationContext} from './components/notification/NotificationProvider'
import Notifications from './components/notification/Notifications'
import ReminderTabView from './components/reminder/ReminderTabView'
import {useRoleContext} from './components/role/RoleProvider'
import Tutorial from './components/tutorial/Tutorial'
import {SystemRole} from './Constants'
import {useAppConfig} from './context/app/AppContext'
import {useUserHospital} from './context/hospital/UserHospitalContext'
import {
    countryToLanguage,
    getLanguageCode,
    languageToCountry,
    setLanguageCode,
} from './i18n/LanguageUtils'
import TranslateUtils from './i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from './utils/BlockScrollsUtils'
import {openPage} from './utils/NavigatorUtils'
import {TopBarBell} from './views/TopBarBell'
const buttonClassName =
    'text-600 hover:text-700 text-sm border-none bg-gray-100 hover:bg-white gap-2'

const iconStyled = {
    height: '20px',
    width: '20px',
    color: 'darkcyan',
}

export const AppTopbar = React.memo(props => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {timezoneId, userRole, hospitalsBelong} = useAppConfig()
    const {currentHospital, onChangeHospital} = useUserHospital()
    const {totalNotifications, totalUnseenNotifications} =
        useNotificationContext()
    const {myRoleName} = useRoleContext()
    //#endregion

    //#region Location URL
    const history = useHistory()
    const location = useLocation()
    //#endregion

    //#region SSO
    const isAuthenticated = useIsAuthenticated()
    const {instance} = useMsal()
    //#endregion

    //#region Refs
    const menu = useRef(null)
    const tutorial = useRef(null)
    const myProfileRef = useRef(null)
    const hospitalsBelongRef = useRef(null)
    const notificationRef = useRef(null)
    const scheduleRef = useRef(null)
    //#endregion

    //#region Language Section
    const {
        translateCommons,
        translateKeys,
        translateLanguage,
        translateDatatable,
    } = TranslateUtils()
    const {i18n} = useTranslation()
    const renderFlagsSelect = (isAvatarBtn = false) => {
        return (
            <div
                className={`${
                    !isAvatarBtn && 'app-top-bar-btn'
                } custom-flags-select`}>
                <ReactFlagsSelect
                    className=" p-0"
                    showOptionLabel={true}
                    selectButtonClassName="py-2"
                    showSelectedLabel={true}
                    placeholder={translateLanguage('Select a language')}
                    countries={['US', 'GB', 'BG']}
                    customLabels={{
                        US: translateLanguage('US - English'),
                        GB: translateLanguage('UK - English'),
                        BG: translateLanguage('Bulgarian'),
                    }}
                    selected={languageToCountry(getLanguageCode())}
                    onSelect={code => {
                        setLanguageCode(countryToLanguage(code))
                        i18n.changeLanguage(countryToLanguage(code))
                    }}
                />
            </div>
        )
    }
    //#endregion

    //#region Tutorial Section
    const renderWatchTutorial = (isShowLabel = false) => {
        return (
            <React.Fragment>
                <Button
                    className={`${buttonClassName} `}
                    icon={<MdPlayLesson style={iconStyled} />}
                    label={translateKeys('Watch tutorials')}
                    onClick={() => tutorial.current.showForm()}
                />
            </React.Fragment>
        )
    }
    //#endregion

    //#region Notification Section
    const notificationPanelVisible = useSignal(false)

    useSignalEffect(() => {
        const contentDiv = document.getElementById(
            'layout-main-container-wrapper',
        )

        toggleBlockScroll(contentDiv, notificationPanelVisible.value)

        return () => {
            if (!!contentDiv) {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    })

    const preventScroll = event => {
        event.preventDefault()
        event.stopPropagation()
    }

    const toggleBlockScroll = useCallback((contentDiv, isVisible) => {
        if (!!contentDiv) {
            if (!!isVisible) {
                contentDiv.addEventListener('wheel', preventScroll, {
                    passive: false,
                })
                contentDiv.addEventListener('touchmove', preventScroll, {
                    passive: false,
                })
            } else {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    }, [])

    const renderNotification = () => {
        return (
            (userRole === SystemRole.ADMIN ||
                userRole === SystemRole.USER_WITH_ROLE) && (
                <React.Fragment>
                    {' '}
                    <Button
                        className={` ${buttonClassName}  my-0 px-3`}
                        icon={
                            <TopBarBell value={totalUnseenNotifications || 0} />
                        }
                        label={translateCommons('Notification')}
                        style={{padding: '0.6rem'}}
                        onClick={e => {
                            notificationRef.current.toggle(e)
                            blockOverflownScroll()
                        }}>
                        <Badge
                            value={totalUnseenNotifications || 0}
                            severity="danger"
                            style={{padding: '1.2px'}}></Badge>
                    </Button>
                    <OverlayPanel
                        ref={notificationRef}
                        onShow={() => {
                            notificationPanelVisible.value =
                                !notificationPanelVisible.value
                            blockOverflownScroll()
                        }}
                        onHide={() => {
                            notificationPanelVisible.value =
                                !notificationPanelVisible.value
                            unblockOverflownScroll()
                        }}
                        className={`apptopbar-overplay-panel col-11 lg:col-6 xl:col-4 `}>
                        <Notifications height={'280px'} removeSpace={true} />
                    </OverlayPanel>
                </React.Fragment>
            )
        )
    }
    //#endregion

    //#region Profile Section
    const renderToggleMenuButton = () => {
        const hospitalLogo = currentHospital?.logo
        return (
            <React.Fragment>
                <div
                    className={`hidden lg:block ${
                        props.isCollapsed ? 'w-5rem' : 'w-12rem'
                    }`}>
                    <Link
                        onClick={() => getSuperAdminRole() && removeClientId()}
                        to={'/'}
                        className="layout-topbar-logo col-12 justify-content-center">
                        {hospitalLogo ? (
                            <img
                                className="h-4rem m-0 mr-2"
                                src={hospitalLogo}
                                alt="logo"
                            />
                        ) : (
                            <Logo className="h-4rem w-9rem m-0" />
                        )}
                    </Link>
                </div>
                <div className="">
                    {userRole !== SystemRole.USER && !getSuperAdminRole() && (
                        <button
                            type="button"
                            className={`p-link layout-topbar-button bg-gray-100 hover:bg-gray-50  lg:ml-5`}
                            onClick={props.onToggleMenuClick}>
                            <i
                                className={`pi pi-bars block md:hidden `}
                                style={{color: 'darkcyan'}}
                            />
                            <i
                                className={` hidden md:block ${
                                    !props.isActiveCollapseMenu
                                        ? 'pi pi-arrow-circle-left '
                                        : 'pi pi-arrow-circle-right '
                                }`}
                                style={{color: 'darkcyan'}}
                            />
                        </button>
                    )}
                </div>
            </React.Fragment>
        )
    }

    const onLogOut = useCallback(async () => {
        try {
            if (!!getRefreshToken()) {
                await revokeToken(getRefreshToken())
            }
        } catch (error) {}

        let isCollapsedAppMenu = getCollapsedAppMenu()
        let isMobileMenuActive = getMobileAppMenuActive()
        clearStorage()
        setCollapsedAppMenu(isCollapsedAppMenu)
        setMobileAppMenuActive(isMobileMenuActive)

        if (!!isAuthenticated) {
            await instance?.logout()
        }

        openPage('/')
    }, [instance, isAuthenticated])

    const roleName = useMemo(() => {
        if (getSuperAdminRole()) {
            return translateKeys('Super Admin')
        }

        switch (userRole) {
            case SystemRole.ADMIN:
                return translateKeys('Admin')
            case SystemRole.USER_WITH_ROLE:
                return translateKeys(myRoleName)
            case SystemRole.USER:
                return translateKeys('Patient')
            default:
                return null
        }
    }, [userRole, myRoleName, translateKeys])

    const renderAvatarButton = useCallback(
        (disabled = false) => {
            return (
                <div className="py-3">
                    <Button
                        className="border-1 avatar-btn m-0 py-2 px-2"
                        onClick={e =>
                            !disabled && myProfileRef.current.toggle(e)
                        }>
                        {' '}
                        {!!getSuperAdminRole() ? (
                            <img
                                className="mr-3"
                                style={{width: '35px', height: '35px'}}
                                src={SuperAdminIcon}
                                alt="logo"
                            />
                        ) : userRole === SystemRole.ADMIN ? (
                            <img
                                className="mr-3"
                                style={{width: '35px', height: '35px'}}
                                src={AdminIcon}
                                alt="logo"
                            />
                        ) : userRole === SystemRole.USER_WITH_ROLE ? (
                            <img
                                className="mr-3"
                                style={{width: '35px', height: '35px'}}
                                src={DoctorIcon}
                                alt="logo"
                            />
                        ) : (
                            userRole === SystemRole.USER && (
                                <img
                                    className="mr-3"
                                    style={{width: '35px', height: '35px'}}
                                    src={PatientIcon}
                                    alt="logo"
                                />
                            )
                        )}
                        <div>
                            <div className="font-bold ellipsis-container">
                                {user?.fullName}
                            </div>
                            <div className="">
                                <Tag
                                    className="p-0 border-round-sm bg-blue-500 px-3"
                                    value={translateDatatable(roleName)}
                                />
                            </div>
                            {!getSuperAdminRole() && (
                                <div className="font-semibold text-xs text-600">
                                    {translateCommons('Time zone')}:{' '}
                                    <span className="text-800">
                                        {timezoneId}
                                    </span>
                                </div>
                            )}
                        </div>
                    </Button>
                </div>
            )
        },
        [
            roleName,
            timezoneId,
            translateCommons,
            translateDatatable,
            user?.fullName,
            userRole,
        ],
    )

    const profileMenuList = useMemo(() => {
        return [
            {
                label: 'My Profile',
                onClick: () =>
                    history.push(`/information/${user?.id}?tv=profiles`),
                isActive: !location.pathname?.includes('/client'),
                icon: <FaUserCircle className="mr-2" style={iconStyled} />,
            },
            {
                label: 'Change Password',
                onClick: () =>
                    history.push(`/information/${user?.id}?tv=security`),
                isActive: true,
                icon: (
                    <RiLockPasswordFill className="mr-2" style={iconStyled} />
                ),
            },
            {
                label: 'Report',
                onClick: () => history.push(`/report`),
                isActive: userRole !== SystemRole.USER && !getSuperAdminRole(),
                icon: <VscReport className="mr-2" style={iconStyled} />,
                isSpace: true,
            },
            {
                label: 'Log Out',
                onClick: () => onLogOut(),
                isActive: true,
                icon: (
                    <RiLogoutCircleRFill className="mr-2" style={iconStyled} />
                ),
            },
        ]
    }, [history, location.pathname, onLogOut, user?.id, userRole])
    //#endregion

    //#region Hospital Section
    const displaySetting = currentHospital?.displaySetting
    const hospitalBanner = useMemo(() => {
        if (!currentHospital || !currentHospital?.id) return null

        const hospitalName = currentHospital?.name
        const hospitalLogo = currentHospital?.logo

        switch (displaySetting) {
            case hospitalDisplaySettingType.imageOnly:
                return
            case hospitalDisplaySettingType.textOnly:
                return hospitalName
            case hospitalDisplaySettingType.textAndImage:
                return hospitalName
            default:
                return
        }

        // if (displaySetting === 'none') {
        //     return null
        // }

        // if (!hospitalLogo && displaySetting !== 'imageOnly') {
        //     return (
        //         <h4 className=" m-0 font-bold text-darkcyan">{hospitalName}</h4>
        //     )
        // }

        // return (
        //     <React.Fragment>
        //         {/* <img
        //             className="h-4rem m-0 mr-2"
        //             src={hospitalLogo}
        //             alt="logo"
        //         /> */}
        //         <h4 className=" m-0 font-bold text-darkcyan">{hospitalName}</h4>
        //     </React.Fragment>
        // )
    }, [currentHospital, displaySetting])

    const renderHospitalsSelection = useCallback(
        (isAvatarBtn = false) => {
            return (
                userRole === SystemRole.USER_WITH_ROLE && (
                    <div className="w-full">
                        <Button
                            className={`${
                                isAvatarBtn
                                    ? 'text-600 hover:text-700 text-sm border-none bg-white hover:bg-gray-50 gap-2'
                                    : buttonClassName
                            }  w-full`}
                            icon={<FaHospitalAlt style={iconStyled} />}
                            label={translateKeys('Change hospital')}
                            onClick={e => {
                                hospitalsBelongRef.current.toggle(e)
                                blockOverflownScroll()
                            }}
                        />
                    </div>
                )
            )
        },
        [translateKeys, userRole],
    )

    const handleOnSelectHospital = useCallback(
        data => {
            onChangeHospital(data)

            if (location.pathname.includes('/patients/')) {
                openPage('/patients')
            } else if (location.pathname.includes('/hcp/')) {
                openPage('/hcp')
            }
        },
        [location.pathname, onChangeHospital],
    )

    // TODO translate here
    const renderListItem = dataItem => {
        return (
            <div className="flex gap-3 align-items-center justify-content-start w-full relative px-3">
                {dataItem?.logo ? (
                    <img
                        className=" w-4rem h-3rem m-0  border-circle"
                        src={dataItem?.logo}
                        alt={dataItem.name}
                        style={{
                            objectFit: 'contain',
                        }}
                    />
                ) : (
                    <Logo className=" w-4rem h-2rem m-0  border-circle" />
                )}
                <div className="text-lg font-semibold">
                    <p className="mb-1">{dataItem.name}</p>
                    {currentHospital?.id === dataItem?.id && (
                        <span className="text-darkcyan">
                            <small>
                                <i className="pi pi-check-circle mr-2"></i>
                            </small>
                            <small>{translateCommons('In View')}</small>
                        </span>
                    )}
                </div>
            </div>
        )
    }
    //#endregion

    const appTopBarMenuList = useMemo(() => {
        return [
            {
                key: 'watchTutorials',
                label: translateKeys('Watch tutorials'),
                icon: <MdPlayLesson style={iconStyled} />,
                onClick: () => tutorial.current.showForm(),
                isHidden: true,
                isActive: true,
            },
            {
                key: 'flag',
                isHidden: true,
                isActive: true,
            },
            {
                key: 'notification',
                label: translateKeys('Notification'),
                icon: <TopBarBell value={10} />,
                onClick: e => {
                    notificationRef.current.toggle(e)
                },
                // badge: totalUnseenNotifications || 0,
                badgeValue: totalUnseenNotifications || 0,
                isActive:
                    userRole === SystemRole.ADMIN ||
                    userRole === SystemRole.USER_WITH_ROLE,
            },
            {
                key: 'changeHospital',
                label: translateKeys('Change hospital'),
                icon: <FaHospitalAlt style={iconStyled} />,
                onClick: e => hospitalsBelongRef.current.toggle(e),
                isHidden: true,
                isActive: userRole === SystemRole.USER_WITH_ROLE,
            },
            {
                key: 'appointment',
                label: translateKeys('Appointment'),
                icon: (
                    <CalendarIcon
                        style={{
                            ...iconStyled,
                        }}
                    />
                ),
                onClick: e => scheduleRef.current.toggle(e),
                isActive: userRole === SystemRole.USER_WITH_ROLE,
            },
        ]
    }, [totalUnseenNotifications, translateKeys, userRole])

    return (
        <React.Fragment>
            <div className="layout-topbar flex justify-content-between gap-3">
                <div className="flex flex-none gap-2 align-items-center">
                    {renderToggleMenuButton()}
                </div>

                <div className="flex flex-grow-1 justify-content-center align-items-center">
                    {/* <div className="flex align-items-center apptopbar-hospital-name">
                        {userRole === SystemRole.USER_WITH_ROLE && (
                            <Link to="/" className="text-lg">
                                {hospitalBanner}
                            </Link>
                        )}
                    </div> */}
                    <div className="flex align-items-center">
                        <div className="apptopbar-menu flex gap-1 align-items-center">
                            {appTopBarMenuList.map((item, idx) =>
                                item.isActive && item.key === 'flag' ? (
                                    <ReactFlagsSelect
                                        className="flag-btn p-0"
                                        showOptionLabel={true}
                                        key={idx}
                                        selectButtonClassName="select-btn-label py-2"
                                        showSelectedLabel={true}
                                        placeholder={translateLanguage(
                                            'Select a language',
                                        )}
                                        countries={['US', 'GB', 'BG']}
                                        customLabels={{
                                            US: translateLanguage(
                                                'US - English',
                                            ),
                                            GB: translateLanguage(
                                                'UK - English',
                                            ),
                                            BG: translateLanguage('Bulgarian'),
                                        }}
                                        selected={languageToCountry(
                                            getLanguageCode(),
                                        )}
                                        onSelect={code => {
                                            setLanguageCode(
                                                countryToLanguage(code),
                                            )
                                            i18n.changeLanguage(
                                                countryToLanguage(code),
                                            )
                                        }}
                                    />
                                ) : (
                                    item.isActive && (
                                        <div
                                            key={idx}
                                            className={`apptopbar-menu-btn ${
                                                item.isHidden && 'hidden-btn'
                                            }  gap-1`}
                                            onClick={item.onClick}>
                                            <span>{item.icon}</span>
                                            <span className="apptopbar-label align-items-center">
                                                {item.label}
                                            </span>
                                            {item.key === 'notification' && (
                                                <Badge
                                                    className="apptopbar-badge"
                                                    value={item.badgeValue}
                                                    severity={
                                                        item.badgeValue < 1
                                                            ? 'secondary'
                                                            : 'danger'
                                                    }
                                                />
                                            )}
                                        </div>
                                    )
                                ),
                            )}
                        </div>
                    </div>
                    <div className="hidden">
                        {' '}
                        <div className="flex flex-none apptopbar-menu align-items-center gap-2 justify-content-center px-2 py-2 bg-gray-100 border-round-lg">
                            {' '}
                            <div className="menu-item hidden md:block">
                                {renderWatchTutorial()}
                            </div>
                            <div className="menu-item md:block">
                                {renderFlagsSelect()}
                            </div>
                            <div className="menu-item md:block">
                                {renderHospitalsSelection()}
                            </div>
                            {userRole === SystemRole.USER_WITH_ROLE && (
                                <div className="menu-item">
                                    <Button
                                        className={`${buttonClassName}`}
                                        icon={
                                            <CalendarIcon
                                                style={{
                                                    ...iconStyled,
                                                }}
                                            />
                                        }
                                        label="Appointment"
                                        onClick={e =>
                                            scheduleRef.current.toggle(e)
                                        }></Button>
                                </div>
                            )}
                            <div className="menu-item">
                                {renderNotification()}
                            </div>
                        </div>
                    </div>

                    <div className=""></div>
                </div>

                <div className="flex flex-none gap-1 align-items-center">
                    <div className="flex">
                        <Button
                            className="p-link layout-topbar-button bg-gray-100 hover:bg-gray-50 block md:hidden text-center"
                            onClick={e => myProfileRef.current.toggle(e)}>
                            <i
                                className="pi pi-ellipsis-v"
                                style={iconStyled}
                            />
                        </Button>

                        <div className="hidden md:block">
                            {renderAvatarButton()}
                        </div>
                    </div>
                </div>
            </div>
            <Tutorial ref={tutorial} />
            {/* Hospital Panel */}
            <OverlayPanel
                ref={hospitalsBelongRef}
                className=""
                onShow={() => blockOverflownScroll()}
                onHide={() => unblockOverflownScroll()}>
                <div className="col-12 ellipsis-container max-w-30rem">
                    <div className="flex flex-column mb-1">
                        <h5 className="mb-0 font-bold">
                            <span>
                                <i className="pi pi-sync mr-3"></i>
                            </span>
                            {translateKeys('Change hospital')}
                        </h5>
                    </div>
                    <hr className="" />
                    <div
                        className="flex flex-column gap-2 p-0 align-items-start justify-content-start overflow-y-scroll"
                        style={{maxHeight: '450px'}}>
                        {hospitalsBelong?.map((item, index) => (
                            <div
                                key={index}
                                className={`col-12 border-round text-sm cursor-pointer ${
                                    currentHospital?.id === item?.id
                                        ? 'bg-gray-50'
                                        : 'hover:surface-50'
                                }`}
                                onClick={() => handleOnSelectHospital(item)}>
                                <div>{renderListItem(item)}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </OverlayPanel>
            {/* MyProfile Menu */}
            <OverlayPanel
                className=""
                ref={myProfileRef}
                onShow={() => blockOverflownScroll()}
                onHide={() => unblockOverflownScroll()}>
                <div className="flex gap-2 flex-column w-15rem">
                    <div className="block md:hidden w-full">
                        {renderAvatarButton(true)}
                        <hr className="my-1" />
                    </div>
                    <div className="block md:hidden  ">
                        {renderFlagsSelect(true)}
                        {renderHospitalsSelection(true)}
                        <hr className="my-1" />
                    </div>

                    {profileMenuList.map(
                        (item, idx) =>
                            item.isActive && (
                                <React.Fragment key={idx}>
                                    {' '}
                                    <Button
                                        className="text-600 hover:text-700 text-sm border-none bg-white hover:bg-gray-50 gap-2 px-1"
                                        onClick={e => {
                                            item.onClick()
                                            myProfileRef.current.toggle(e)
                                        }}
                                        icon={item.icon}
                                        label={translateKeys(item.label)}
                                    />
                                    {item.isSpace && <hr className="my-1" />}
                                </React.Fragment>
                            ),
                    )}
                </div>
            </OverlayPanel>
            {/* MyProfile Menu */}
            <OverlayPanel
                className="overlay-panel-custom-z-index"
                style={{width: '50rem'}}
                ref={scheduleRef}
                dismissable
                onShow={() => blockOverflownScroll()}
                onHide={() => unblockOverflownScroll()}>
                <ReminderTabView />
            </OverlayPanel>

            {/* Notification */}
            {(userRole === SystemRole.ADMIN ||
                userRole === SystemRole.USER_WITH_ROLE) && (
                <React.Fragment>
                    {/* <Button
                        className={` ${buttonClassName}  my-0 px-3`}
                        icon={<TopBarBell value={totalUnseenNotifications || 0} />}
                        label="Notification"
                        style={{padding: '0.6rem'}}
                        onClick={e => {
                            notificationRef.current.toggle(e)
                        }}>
                        <Badge
                            value={totalUnseenNotifications || 0}
                            severity="danger"
                            style={{padding: '1.2px'}}></Badge>
                    </Button> */}
                    <OverlayPanel
                        ref={notificationRef}
                        onShow={() => {
                            notificationPanelVisible.value =
                                !notificationPanelVisible.value
                            blockOverflownScroll()
                        }}
                        onHide={() => {
                            notificationPanelVisible.value =
                                !notificationPanelVisible.value
                            unblockOverflownScroll()
                        }}
                        className={`apptopbar-overplay-panel `}>
                        <Notifications height={'280px'} removeSpace={true} />
                    </OverlayPanel>
                </React.Fragment>
            )}
        </React.Fragment>
    )
})
